import { useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { CalendarIcon, X } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

dayjs.extend(customParseFormat);

/** Parse a free-text date string using common formats. Returns a valid dayjs or null. */
function parseInputDate(raw: string): dayjs.Dayjs | null {
  const formats = [
    "DD/MM/YYYY",
    "D/M/YYYY",
    "D/M/YY",
    "YYYY-MM-DD",
    "MM/DD/YYYY",
    "D MMM YYYY",
    "D MMMM YYYY",
  ];
  for (const fmt of formats) {
    const parsed = dayjs(raw, fmt, true);
    if (parsed.isValid()) return parsed;
  }
  const loose = dayjs(raw);
  return loose.isValid() ? loose : null;
}

interface DateRangePickerProps {
  value?: any; // Can be object {from, to} or string "from,to"
  onChange?: (value: { from: string; to: string }) => void;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  dateFormat?: string;
  displayFormat?: string; // Format for displaying in trigger
  disabled?: boolean;
  numberOfMonths?: number;
}

export function DateRangePicker({
  value,
  onChange,
  placeholder: _placeholder = "Select date range",
  className,
  triggerClassName,
  dateFormat = "YYYY-MM-DD",
  displayFormat: _displayFormat = "D MMM YY",
  disabled = false,
  numberOfMonths = 1,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  // Raw keyboard input for from/to; null means show formatted value
  const [fromInputText, setFromInputText] = useState<string | null>(null);
  const [toInputText, setToInputText] = useState<string | null>(null);

  // Parse value from object {from, to} or string "from,to" format
  const parseValue = (
    fieldValue?: any,
  ): [string | undefined, string | undefined] => {
    if (typeof fieldValue === "object" && fieldValue !== null) {
      // Object format: {from: "2026-01-05", to: "2026-01-08"}
      return [fieldValue.from, fieldValue.to];
    }
    if (typeof fieldValue === "string" && fieldValue) {
      // String format: "2026-01-05,2026-01-08"
      const [start, end] = fieldValue.split(",");
      return [start?.trim(), end?.trim()];
    }
    return [undefined, undefined];
  };

  const [startDate, endDate] = parseValue(value);

  const selectedRange: DateRange = {
    from: startDate ? dayjs(startDate).toDate() : undefined,
    to: endDate
      ? dayjs(endDate).toDate()
      : startDate
        ? dayjs(startDate).toDate()
        : undefined,
  };

  const handleSelect = (range: DateRange | undefined) => {
    if (!range || (!range.from && !range.to)) {
      onChange?.({ from: "", to: "" });
      return;
    }

    if (range.from) {
      const formattedStart = dayjs(range.from).format(dateFormat);
      const formattedEnd = range.to
        ? dayjs(range.to).format(dateFormat)
        : formattedStart;

      const newValue = {
        from: formattedStart,
        to: formattedEnd,
      };

      // Always update value to show selection
      onChange?.(newValue);
      setFromInputText(null);
      setToInputText(null);

      // Close popover only when complete range is selected (both dates and different)
      if (range.to && range.from.getTime() !== range.to.getTime()) {
        setTimeout(() => setOpen(false), 200);
      }
    }
  };

  const handleClear = () => {
    onChange?.({ from: "", to: "" });
    setFromInputText(null);
    setToInputText(null);
    setOpen(false);
  };

  const handleFromInput = (raw: string) => {
    setFromInputText(raw);
    if (raw === "") {
      onChange?.({ from: "", to: endDate ?? "" });
    } else {
      const parsed = parseInputDate(raw);
      if (parsed) {
        onChange?.({ from: parsed.format(dateFormat), to: endDate ?? "" });
      }
    }
  };

  const handleToInput = (raw: string) => {
    setToInputText(raw);
    if (raw === "") {
      onChange?.({ from: startDate ?? "", to: "" });
    } else {
      const parsed = parseInputDate(raw);
      if (parsed) {
        onChange?.({ from: startDate ?? "", to: parsed.format(dateFormat) });
      }
    }
  };

  return (
    <div className={cn("aero:w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        {/* Two text inputs (From / To) with a separator and a calendar icon */}
        <div
          className={cn(
            "aero:flex aero:h-10 aero:w-full aero:items-center aero:rounded aero:border aero:border-neutral-300 aero:bg-white aero:transition-colors aero:focus-within:ring-1 aero:focus-within:ring-primary aero:focus-within:border-primary",
            disabled &&
              "aero:opacity-50 aero:pointer-events-none aero:bg-neutral-50",
            triggerClassName,
          )}
        >
          <input
            type="text"
            disabled={disabled}
            placeholder="DD/MM/YYYY"
            aria-label="Start date"
            value={
              fromInputText !== null
                ? fromInputText
                : startDate
                  ? dayjs(startDate).format("DD/MM/YYYY")
                  : ""
            }
            onChange={(e) => handleFromInput(e.target.value)}
            onBlur={() => setFromInputText(null)}
            className="aero:flex-1 aero:min-w-0 aero:h-full aero:bg-transparent aero:pl-3 aero:pr-1 aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-neutral-900 aero:placeholder:text-neutral-500 aero:outline-none"
          />
          <span className="aero:text-neutral-400 aero:text-xs aero:select-none aero:px-1">
            —
          </span>
          <input
            type="text"
            disabled={disabled}
            placeholder="DD/MM/YYYY"
            aria-label="End date"
            value={
              toInputText !== null
                ? toInputText
                : endDate
                  ? dayjs(endDate).format("DD/MM/YYYY")
                  : ""
            }
            onChange={(e) => handleToInput(e.target.value)}
            onBlur={() => setToInputText(null)}
            className="aero:flex-1 aero:min-w-0 aero:h-full aero:bg-transparent aero:pl-1 aero:pr-1 aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-neutral-900 aero:placeholder:text-neutral-500 aero:outline-none"
          />
          <div className="aero:flex aero:items-center aero:gap-1 aero:shrink-0 aero:pr-2">
            {startDate && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="aero:rounded-sm aero:opacity-70 hover:aero:opacity-100 aero:transition-opacity"
                aria-label="Clear date range"
              >
                <X className="aero:h-3 aero:w-3" />
              </button>
            )}
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (!disabled) setOpen(!open);
                }}
                className="aero:text-neutral-500 hover:aero:text-neutral-700 aero:transition-opacity aero:outline-none"
                aria-label="Open calendar picker"
              >
                <CalendarIcon className="aero:h-5 aero:w-5" />
              </button>
            </PopoverTrigger>
          </div>
        </div>
        <PopoverContent
          className="aero:w-auto aero:p-0 aero:z-[9999]"
          align="start"
          sideOffset={4}
          alignOffset={0}
          avoidCollisions={true}
          collisionPadding={8}
        >
          <Calendar
            mode="range"
            selected={selectedRange}
            onSelect={handleSelect}
            initialFocus
            numberOfMonths={numberOfMonths}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
